import React, { useState } from 'react';
import CommonModal from '../../../../Components/CommonModal';
import Section from './Section';
import { risk_portal_save_bowtie } from '../../../../services';
import { body, bodySmall, label as labelPreset } from '../../../../Constants/Fonts';

// BowtieSection — bow-tie analysis: threats → [preventive barriers] → top event
// → [mitigating barriers] → consequences. Its single "Save & Next" button saves
// the bow-tie then advances the workflow (via ctx.submitStage).
export default function BowtieSection({ riskId, bowtie, submitStage, canSubmit, stageBtnText, busy }) {
    const bt = bowtie || {};
    // Live form values (from the edit form's onValuesChange) so the columns
    // above recompute as the user types. Null until first edit → falls back to
    // the saved bow-tie.
    const [live, setLive] = useState(null);

    // Parse the one-item-per-line / name|effectiveness% textareas into the
    // structured payload the API expects.
    const saveBowtie = (data) => risk_portal_save_bowtie(riskId, {
        top_event: (data.top_event || '').trim(),
        threats: parseItems(data.threats || ''),
        consequences: parseItems(data.consequences || ''),
        preventive_barriers: parseBarriers(data.preventive || ''),
        mitigating_barriers: parseBarriers(data.mitigating || ''),
    });

    // The summary columns source from live edits when present, else the saved
    // bow-tie — so they update as you type, before saving.
    const src = live ? {
        top_event: live.top_event || '',
        threats: parseItems(live.threats || ''),
        preventive_barriers: parseBarriers(live.preventive || ''),
        mitigating_barriers: parseBarriers(live.mitigating || ''),
        consequences: parseItems(live.consequences || ''),
    } : bt;

    return (
        <Section title="Bow-Tie Analysis">
            {src.top_event && <p style={body({ color: 'var(--color-text-primary)', margin: '0 0 12px' })}><strong>Top event:</strong> {src.top_event}</p>}
            <div className="rp1_bowtie_grid">
                <Column title="Threats" items={(src.threats || []).map((t) => t.name)} />
                <Column title="Preventive Barriers" items={(src.preventive_barriers || []).map((b) => `${b.name} (${b.effectiveness || 0}%)`)} />
                <Column title="Mitigating Barriers" items={(src.mitigating_barriers || []).map((b) => `${b.name} (${b.effectiveness || 0}%)`)} />
                <Column title="Consequences" items={(src.consequences || []).map((c) => c.name)} />
            </div>

            {/* Bow-Tie edit form — only for the active stage owner; the columns
                above stay as a read-only view otherwise. Single button saves
                then advances. */}
            {canSubmit && (
                <CommonModal
                    inline
                    title="Edit Bow-Tie Analysis"
                    subtitle="One item per line. Barriers use the format name|effectiveness%."
                    fields={[
                        { name: 'top_event', label: 'Top Event', type: 'text', placeholder: 'The central risk event' },
                        { name: 'threats', label: 'Threats (one per line)', type: 'textarea', rows: 1, placeholder: 'Phishing Insider misuse' },
                        { name: 'preventive', label: 'Preventive Barriers (name|effectiveness%)', type: 'textarea', rows: 1, placeholder: 'MFA|75 Email filtering|50' },
                        { name: 'mitigating', label: 'Mitigating Barriers (name|effectiveness%)', type: 'textarea', rows: 1, placeholder: 'Incident Response|75 Insurance|50' },
                        { name: 'consequences', label: 'Consequences (one per line)', type: 'textarea', rows: 1, placeholder: 'Data breach Regulatory fine' },
                    ]}
                    defaultValues={{
                        top_event: bt.top_event || '',
                        threats: itemsToText(bt.threats),
                        preventive: barriersToText(bt.preventive_barriers),
                        mitigating: barriersToText(bt.mitigating_barriers),
                        consequences: itemsToText(bt.consequences),
                    }}
                    onValuesChange={setLive}
                    onSubmit={(data) => submitStage(() => saveBowtie(data))}
                    buttons={[{ text: stageBtnText, variant: 'primary', type: 'submit', loading: busy, loadingText: 'Saving…' }]}
                />
            )}
        </Section>
    );
}

function Column({ title, items }) {
    return (
        <div>
            <div style={labelPreset({ color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 })}>{title}</div>
            {items.length === 0
                ? <div style={bodySmall({ color: 'var(--color-text-muted)' })}>—</div>
                : <ul className="rp1_bowtie_list">{items.map((it, i) => <li key={i} style={body({ color: 'var(--color-text-secondary)' })}>{it}</li>)}</ul>}
        </div>
    );
}

const itemsToText = (arr) => (arr || []).map((x) => x.name).join('\n');
const barriersToText = (arr) => (arr || []).map((b) => `${b.name}|${b.effectiveness || 50}`).join('\n');
const parseItems = (text) => text.split('\n').filter((l) => l.trim()).map((l, i) => ({ id: i + 1, name: l.trim() }));
const parseBarriers = (text) => text.split('\n').filter((l) => l.trim()).map((l, i) => {
    const [name, eff] = l.split('|');
    return { id: i + 1, name: name.trim(), effectiveness: parseInt(eff, 10) || 50, status: 'active' };
});

